/**
 * Hybrid Rendering Hook
 * Manages hybrid Canvas+SVG rendering for optimal performance
 * Static elements (parking spaces) render on Canvas, dynamic elements (icons) on SVG
 */

import { useEffect, useRef, useCallback } from 'react'
import { dia } from '@joint/core'
import { useCSVStore } from '../../../map-editor-features/csv'
import { useFloorStore } from '../../../map-editor-shared/store/floorStore'
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'
import { createElementsFromCSV } from '../lib/csvRenderer'
import { HybridRenderer } from '../lib/hybridRenderer'
import { normalizeMapScale } from '../../../map-editor-features/editor/lib/normalizeMapScale'
import { perfMonitor } from '../../../map-editor-shared/lib/performanceMonitor'
import { getFlag } from '../../../config/featureFlags'

interface UseHybridRenderingOptions {
  enabled?: boolean  // Feature flag to enable/disable hybrid rendering
  staticThreshold?: number  // Number of elements before switching to hybrid
}

export function useHybridRendering(
  graph: dia.Graph | null,
  paper: dia.Paper | null,
  container: HTMLElement | null,
  setElementCount: (count: number) => void,
  setObjectsByLayer: (layers: Map<string, dia.Element[]>) => void,
  _setLoadedFileName: (name: string | null) => void,
  pendingGraphJson: any | null,
  setPendingGraphJson: (json: any | null) => void,
  _isRestoring: boolean = false,
  options: UseHybridRenderingOptions = {}
) {
  const {
    enabled = true,  // Enable by default
    staticThreshold = 100  // Use hybrid if >100 static elements (lowered from 500)
  } = options

  const groupedLayers = useCSVStore(state => state.groupedLayers)
  const selectedLayers = useCSVStore(state => state.selectedLayers)
  const uploadState = useCSVStore(state => state.uploadState)
  const currentFloor = useFloorStore(state => state.currentFloor)
  const renderScale = useFloorStore(state => {
    const floor = state.currentFloor ? state.floors.find(f => f.id === state.currentFloor) : undefined
    return floor?.mapData?.renderScale ?? 1500
  })
  const mappings = useObjectTypeStore(state => state.mappings)
  const mappingsLoading = useObjectTypeStore(state => state.mappingsLoading)
  const currentLoadingFloorId = useObjectTypeStore(state => state.currentLoadingFloorId)

  // Hybrid renderer instance
  const hybridRendererRef = useRef<HybridRenderer | null>(null)

  // Track if hybrid mode is active
  const isHybridActiveRef = useRef(false)

  // Track last renderScale to detect changes
  const lastRenderScaleRef = useRef<number | null>(null)
  const lastMappingsLoadingRef = useRef<boolean>(false)

  // Track last upload state
  const lastUploadStateRef = useRef<'idle' | 'uploading' | 'parsing' | 'parsed' | 'error'>('idle')

  // Track last isRestored flag to detect floor switches
  const lastIsRestoredRef = useRef<boolean>(false)

  // Track if viewport fitting was already done FOR THIS DATASET
  // Reset when new CSV is uploaded, but not on every render
  const viewportFittedRef = useRef(false)
  const renderReadyRef = useRef(true)

  // Initialize hybrid renderer
  useEffect(() => {
    if (!enabled || !graph || !paper || !container) return

    // Create hybrid renderer if not exists
    if (!hybridRendererRef.current) {
      hybridRendererRef.current = new HybridRenderer({
        canvasWidth: 10000,  // Reduced from 100000 (10x smaller = 100x faster clearRect)
        canvasHeight: 10000, // Sufficient for typical parking lot maps
        staticTypes: ['parking_space', 'background', 'wall', 'pillar'],
        dynamicTypes: ['cctv', 'sensor', 'icon', 'vehicle', 'person'],
      })

      // Initialize with paper and container (async)
      hybridRendererRef.current.initialize(paper, container).then(() => {
      }).catch(error => {
      })
    }

    return () => {
      if (hybridRendererRef.current) {
        hybridRendererRef.current.destroy()
        hybridRendererRef.current = null
        isHybridActiveRef.current = false
      }
    }
  }, [enabled, graph, paper, container])

  const isFloorSwitching = useFloorStore(state => state.isFloorSwitching)

  // Handle floor changes - clear graph and hybrid renderer
  // Skip during floor restoration to avoid wiping data that useFloorSwitching just loaded via fromJSON
  useEffect(() => {
    if (!graph || !currentFloor) return
    if (isFloorSwitching) return

    // Track floor changes
    const lastFloor = lastFloorRef.current

    // If floor changed, clear the graph and hybrid renderer
    if (lastFloor !== null && lastFloor !== currentFloor) {

      // Clear the graph
      graph.clear()

      // Clear hybrid renderer if active
      if (hybridRendererRef.current) {
        hybridRendererRef.current.clear()
      }

      // Reset viewport fitted flag
      viewportFittedRef.current = false

      // Clear element count and layers
      setElementCount(0)
      setObjectsByLayer(new Map())

      // CRITICAL: Update lastFloorRef to prevent duplicate processing in rendering useEffect
      lastFloorRef.current = currentFloor
    }
  }, [currentFloor, graph, isFloorSwitching, setElementCount, setObjectsByLayer])

  // Handle CSV rendering
  const renderCSVData = useCallback(() => {
    if (!graph || !paper || !groupedLayers || groupedLayers.length === 0) {
      return
    }

    // CRITICAL FIX: Block rendering while layer mappings are loading
    // Prevents race condition where CSV data from new floor renders with old floor's mappings
    if (mappingsLoading || (currentLoadingFloorId && currentLoadingFloorId === currentFloor)) {
      return
    }

    const startTime = performance.now()


    // Clear existing elements
    graph.clear()
    if (hybridRendererRef.current) {
      hybridRendererRef.current.clear()
    }

    // CRITICAL FIX: Reset viewport fitted flag when clearing graph for new CSV data
    // Without this, viewport fitting won't happen after the first render
    viewportFittedRef.current = false

    // CRITICAL: Get both entity mappings AND layer type mappings for current floor
    // Use String() to ensure type consistency (prevent string vs number mismatch)
    const floorMappings = currentFloor
      ? mappings.filter(m => String(m.floorId) === String(currentFloor))
      : []

    // Get layer type mappings for this floor
    const layerTypeMappings = useObjectTypeStore.getState().getLayerTypeMappingsByFloorId(currentFloor || '')

    // Create a map of layer name to type ID
    const layerToTypeMap = new Map<string, string>()
    layerTypeMappings.forEach(ltm => {
      layerToTypeMap.set(ltm.layerName, ltm.typeId)
    })

    const mappedEntityHandles = new Set(floorMappings.map(m => m.entityHandle))

    // CRITICAL DEBUG: Log floor mappings with detailed information

    // Get selected layers - render all selected layers (with or without mappings)
    // Layers without mappings render with default styles
    const layersToRender = groupedLayers.filter(layer => {
      return selectedLayers.has(layer.layer)
    })

    if (layersToRender.length === 0) {
      setElementCount(0)
      setObjectsByLayer(new Map())
      return
    }

    // Calculate bounds from all entities
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
    layersToRender.forEach(layer => {
      layer.entities.forEach(entity => {
        entity.points.forEach(point => {
          minX = Math.min(minX, point.x)
          maxX = Math.max(maxX, point.x)
          minY = Math.min(minY, point.y)
          maxY = Math.max(maxY, point.y)
        })
      })
    })

    const bounds = { minX, maxX, minY, maxY }

    // Create type mappings - combine entity mappings AND layer type mappings
    const types = useObjectTypeStore.getState().types
    const typeMappings: Array<{ entityHandle: string; type: any }> = []

    // First, add all entity-specific mappings
    floorMappings.forEach(m => {
      const type = types.find(t => t.id === m.typeId)
      if (type) {
        typeMappings.push({
          entityHandle: m.entityHandle,
          type: type
        })
      }
    })

    // CRITICAL FIX: Only add layer type mappings for entities that belong to current floor
    // We verify this by checking if the layer was included due to layer type mapping
    const mappedEntityHandlesSet = new Set(typeMappings.map(tm => tm.entityHandle))

    // Track how many entities we process per layer for debugging
    const layerProcessingStats = new Map<string, { total: number; mapped: number; layerTyped: number }>()

    layersToRender.forEach(layer => {
      const layerTypeId = layerToTypeMap.get(layer.layer)
      const stats = { total: layer.entities.length, mapped: 0, layerTyped: 0 }

      if (layerTypeId) {
        const type = types.find(t => t.id === layerTypeId)
        if (type) {
          // CRITICAL: Only add layer type mappings for entities in this layer
          // that don't already have entity-specific mappings
          layer.entities.forEach(entity => {
            if (mappedEntityHandlesSet.has(entity.entityHandle)) {
              stats.mapped++
            } else {
              typeMappings.push({
                entityHandle: entity.entityHandle,
                type: type
              })
              stats.layerTyped++
            }
          })
        }
      } else {
        // Count how many entities have entity-specific mappings
        layer.entities.forEach(entity => {
          if (mappedEntityHandlesSet.has(entity.entityHandle)) {
            stats.mapped++
          }
        })
      }

      layerProcessingStats.set(layer.layer, stats)
    })

    // Log processing stats to debug entity count mismatch

    // CRITICAL DEBUG: Log type mappings with color information

    // Create elements from CSV
    const result = createElementsFromCSV(
      layersToRender,
      bounds,
      typeMappings,
      renderScale,
      undefined,
      layerTypeMappings
    )

    const elements = result.elements
    const layerMap = result.objectsByLayer

    // CANVAS_EDITOR: the unified CanvasRenderer overlay draws everything from the
    // live graph, so the legacy hybrid canvas would double-render. Skip it and keep
    // the paper frozen (same configuration as the saved-map fromJSON path).
    const canvasEditor = getFlag('CANVAS_EDITOR')

    // Decide whether to use hybrid rendering based on element count (skip per-element classification)
    const useHybrid = enabled && hybridRendererRef.current && elements.length > staticThreshold && !canvasEditor

    // Elements are already sorted by priority from createElementsFromCSV
    const sortedElements = elements

    // Batch addition with freeze/unfreeze for performance
    // Remove silent: true to ensure DOM rendering happens
    renderReadyRef.current = false
    paper.freeze()
    graph.addCells(sortedElements)

    // CRITICAL: Check if paper is still valid before unfreezing
    // Paper can be removed during HMR or rapid component unmount/remount
    // CANVAS_EDITOR: never unfreeze — unfreezing would build SVG views for every
    // cell and bring back the pre-Canvas DOM cost right after import.
    if (!canvasEditor) {
      try {
        // Increase batchSize for large datasets (100 -> 500) to reduce DOM update frequency
        paper.unfreeze({ batchSize: 500 })
      } catch (error) {
        // Paper was removed - this can happen during HMR or component cleanup
        return
      }
    }

    if (useHybrid) {
      // Use hybrid rendering AFTER adding to graph
      isHybridActiveRef.current = true

      perfMonitor.start('hybrid-render')
      hybridRendererRef.current!.processElements(elements)
      perfMonitor.end('hybrid-render')
    } else {
      // Traditional SVG rendering
      isHybridActiveRef.current = false
    }

    // Update counts and layer map
    setElementCount(elements.length)
    setObjectsByLayer(layerMap)

    // Center view on content

    if (elements.length > 0) {
      // Use the calculated bounds from CSV data (more reliable than graph.getBBox() immediately after addCells)
      const bbox = {
        x: bounds.minX,
        y: bounds.minY,
        width: bounds.maxX - bounds.minX,
        height: bounds.maxY - bounds.minY
      }


      if (bbox && bbox.width > 0 && bbox.height > 0 && !viewportFittedRef.current) {

        // Fit viewport once per dataset (prevents infinite loop from paper transform events)
        // Use requestAnimationFrame to ensure DOM is fully ready (same as useLayerRendering)
        requestAnimationFrame(() => {

          // Use scaleContentToFit for both SVG and Hybrid modes
          // This is the stable, verified approach from useLayerRendering
          paper.scaleContentToFit({
            padding: 100,
            maxScale: 2,
            minScale: 0.05,
            useModelGeometry: true,
          })
          renderReadyRef.current = true

          // For hybrid mode, redraw after scaleContentToFit completes
          if (useHybrid && hybridRendererRef.current) {
            // Use another requestAnimationFrame to ensure scaleContentToFit has fully applied
            requestAnimationFrame(() => {
              hybridRendererRef.current!.redraw()
            })
          }
        })

        // Mark as fitted to prevent re-triggering on subsequent renders
        viewportFittedRef.current = true
      } else {
      }
    } else {
    }

    const totalTime = performance.now() - startTime


  }, [graph, paper, groupedLayers, selectedLayers, mappings, renderScale, enabled, staticThreshold, setElementCount, setObjectsByLayer, currentFloor, mappingsLoading, currentLoadingFloorId])

  // Track last floor to detect floor changes
  const lastFloorRef = useRef<string | null>(null)

  // Watch for changes and trigger rendering
  useEffect(() => {
    if (!graph || !paper || !groupedLayers || groupedLayers.length === 0) return
    if (isFloorSwitching) return

    // CRITICAL: Skip if mappings are still loading (race condition prevention)
    if (mappingsLoading || (currentLoadingFloorId && currentLoadingFloorId === currentFloor)) {
      return
    }

    // CRITICAL: Skip CSV rendering if current floor has saved graphJson.
    //
    // PRESERVATION INVARIANT (mirrors decideRecreateCells.ts):
    //   When the floor has a saved graphJson — including the dashboard re-entry
    //   path where useFloorSwitching marks CSV as `isRestored: true` BEFORE
    //   calling graph.fromJSON() — re-running csvRenderer wipes the user's
    //   authored data (Excel-applied properties, edited names, relationships).
    //   graphJson always wins. Only fresh CSV uploads (no graphJson yet) or
    //   render-scale changes (intentional geometry recompute) may run csvRenderer.
    //
    // Check floorStore directly to avoid timing issues with pendingGraphJson.
    const currentFloorData = currentFloor ? useFloorStore.getState().floors.find(f => f.id === currentFloor) : undefined
    const hasGraphJson = currentFloorData?.mapData?.graphJson &&
                        currentFloorData.mapData.graphJson.cells &&
                        currentFloorData.mapData.graphJson.cells.length > 0

    // Compute renderScaleChanged BEFORE the skip check (consumed below too).
    const renderScaleChanged =
      lastRenderScaleRef.current !== null && lastRenderScaleRef.current !== renderScale

    if (hasGraphJson && !renderScaleChanged) {
      return
    }

    // Check if floor changed
    const floorChanged = lastFloorRef.current !== null && lastFloorRef.current !== currentFloor
    lastFloorRef.current = currentFloor

    // Check if we just completed a CSV upload (fresh upload, not restoration)
    const currentStatus = uploadState.status
    const isRestored = (uploadState as any).isRestored || false
    const justUploaded = lastUploadStateRef.current === 'parsing' && currentStatus === 'parsed' && !isRestored
    lastUploadStateRef.current = currentStatus

    // Check if CSV was just restored from floor switch
    const justRestored = currentStatus === 'parsed' && isRestored && !lastIsRestoredRef.current
    lastIsRestoredRef.current = isRestored

    // renderScaleChanged is already computed above (used by the skip check).
    // Update the ref now that the decision has been made.
    lastRenderScaleRef.current = renderScale

    // Check if mappings just finished loading (transition detection)
    const mappingsJustLoaded = lastMappingsLoadingRef.current === true && !mappingsLoading
    lastMappingsLoadingRef.current = mappingsLoading

    // Render conditions:
    // 1. Just uploaded CSV (fresh upload)
    // 2. Just restored CSV (floor switch)
    // 3. RenderScale changed
    // 4. Floor changed (to ensure proper cleanup and re-render)
    // 5. Mappings just finished loading (transition, not steady state)
    // 6. Layer selection/mappings changed while already parsed
    const shouldRender = justUploaded ||
                        justRestored ||
                        renderScaleChanged ||
                        floorChanged ||
                        mappingsJustLoaded ||
                        (uploadState.status === 'parsed' && (selectedLayers.size > 0 || mappings.length > 0))

    if (shouldRender) {
      // Reset viewport fitted flag for new data
      if (justUploaded || justRestored || renderScaleChanged || floorChanged) {
        viewportFittedRef.current = false
      }


      renderCSVData()
    }
  }, [uploadState, renderScale, selectedLayers, mappings, renderCSVData, graph, paper, groupedLayers, currentFloor, isFloorSwitching, mappingsLoading, currentLoadingFloorId, pendingGraphJson])

  // Handle pending graph JSON (floor switch)
  useEffect(() => {
    if (!graph || !paper || !pendingGraphJson) return

    // CRITICAL: Don't restore empty graphs - this would clear CSV-rendered maps
    if (!pendingGraphJson.cells || pendingGraphJson.cells.length === 0) {
      setPendingGraphJson(null)
      return
    }

    try {
      // Restore from JSON
      renderReadyRef.current = false
      graph.fromJSON(pendingGraphJson)
      setPendingGraphJson(null)

      // 표준 비율 보정 — 일반 주차구역 단변(반포 표준 198.32px) 기준으로 전체 스케일 일치.
      // paper 도 함께 전달해 view-level scale 자동 보정 (화면 크기는 보정 전과 동일).
      // idempotent — 이미 표준 크기에 가까우면 skip.
      normalizeMapScale(graph, paper)

      const cells = graph.getCells()
      setElementCount(cells.length)

      // Reconstruct layer map
      const layerMap = new Map<string, dia.Element[]>()
      cells.forEach((cell) => {
        if (cell.isElement()) {
          const element = cell as dia.Element
          const layer = element.get('data')?.layer || element.get('layer') || 'default'
          const layerElements = layerMap.get(layer) || []
          layerElements.push(element)
          layerMap.set(layer, layerElements)
        }
      })
      setObjectsByLayer(layerMap)


      // CRITICAL FIX: Fit viewport to restored content
      // Without this, restored graph appears off-screen after floor switch
      if (cells.length > 0) {
        requestAnimationFrame(() => {
          const bbox = graph.getBBox()

          if (bbox && bbox.width > 0 && bbox.height > 0) {

            paper.scaleContentToFit({
              padding: 100,
              maxScale: 2,
              minScale: 0.05,
              useModelGeometry: true,
            })
            renderReadyRef.current = true

            // For hybrid mode, redraw after viewport fitting
            if (hybridRendererRef.current && isHybridActiveRef.current) {
              requestAnimationFrame(() => {
                hybridRendererRef.current!.redraw()
              })
            }
          }
        })
      }
    } catch (error) {
    }
  }, [pendingGraphJson, graph, paper, setPendingGraphJson, setElementCount, setObjectsByLayer])

  // Expose hybrid renderer stats
  const getHybridStats = useCallback(() => {
    if (!hybridRendererRef.current || !isHybridActiveRef.current) {
      return null
    }
    return hybridRendererRef.current.getStats()
  }, [])

  // Canvas hit-test for click selection (used by useCanvasClickSelection)
  const canvasHitTest = useCallback((clientX: number, clientY: number): string | null => {
    if (!hybridRendererRef.current || !isHybridActiveRef.current) return null
    return hybridRendererRef.current.hitTestFromClient(clientX, clientY)
  }, [])

  // Get canvas-rendered elements for fallback bbox hit-testing
  const getCanvasElements = useCallback(() => {
    if (!hybridRendererRef.current || !isHybridActiveRef.current) return null
    return hybridRendererRef.current.getCanvasElements()
  }, [])

  // Force re-render
  const forceRender = useCallback(() => {
    if (hybridRendererRef.current && isHybridActiveRef.current) {
      hybridRendererRef.current.redraw()
    } else if (paper && paper.options.width && paper.options.height) {
      // Force update by triggering a resize event
      paper.setDimensions(paper.options.width, paper.options.height)
    }
  }, [paper])

  return {
    isHybridActive: isHybridActiveRef.current,
    getHybridStats,
    forceRender,
    renderCSVData,
    renderReady: renderReadyRef,
    canvasHitTest,
    getCanvasElements
  }
}